import { useMemo } from 'react';
import { blue, orange, white } from '@carbon/colors';
import { Layer, Source } from 'react-map-gl';
import {
  ltdSlotsTileUrl,
  packageMapSourceUrl,
} from 'pages/packages/show/eval3-new/utils';
import type { RejectedPlace } from './coverage-preview';
import { RejectedPlaceTooltip } from './rejected-place-tooltip';
import {
  LTD_PROPERTY,
  REJECTED,
  REJECTED_LAYER_IDS,
  rejectedFilter,
} from './rejected-place-utils';

// Dark blue (Carbon blue 70), not the map's covered green: a slot line drawn over the green
// region in the same green would disappear into it. Not valued is strong orange (Carbon orange
// 40) with a thin white halo, so it reads on the red stack and on the green alike.
const COVERED = blue[70];
const UNCOVERED = orange[40];
const HALO = white;
const STICK_WIDTH = 1.5;
const HALO_WIDTH = 1;

interface SlotOverlayProps {
  packageId: number;
  /** The Type Curve scenario the preview counted on (its resolved typeCurveScenarioId). */
  typeCurveScenarioId: number;
  /** The preview's slot ids: a well's is its uwi. */
  coveredSlotIds: readonly string[];
  uncoveredSlotIds: readonly string[];
  /** The preview's LTD slots, by IUFS row id. */
  coveredIufsIds: readonly number[];
  uncoveredIufsIds: readonly number[];
  /** The places the TC team rejected, by IUFS row id, with their reason. */
  rejectedPlaces: readonly RejectedPlace[];
}

/**
 * Filter and colour for one tile: only the preview's slots, dark blue where covered, orange with a
 * white halo where not.
 *
 * @param {string} property - The tile property the ids match.
 * @param {Array} covered - The covered ids.
 * @param {Array} uncovered - The uncovered ids.
 * @return {object} The layer filter, the colour, the not-valued filter (the sticks' halo layer)
 *   and the halo width (the points' stroke).
 */
export function slotPaint(
  property: string,
  covered: readonly (string | number)[],
  uncovered: readonly (string | number)[]
) {
  const get = ['get', property];
  const isCovered = ['in', get, ['literal', [...covered]]];
  return {
    filter: ['in', get, ['literal', [...covered, ...uncovered]]] as never,
    color: ['case', isCovered, COVERED, UNCOVERED] as never,
    uncoveredFilter: ['in', get, ['literal', [...uncovered]]] as never,
    haloWidth: ['case', isCovered, 0, HALO_WIDTH] as never,
  };
}

/**
 * The package's slots over the case map, coloured by whether the picks cover them, from the
 * coverage preview. Wells come from the eval page's well source and match by `uwi`. LTD slots
 * come from the scenario-mode LTD tile, a superset across Type Curve versions, and match by IUFS
 * row id, as EvalModalLtdLayer does: a composite `slot_id` can name a slot in another version.
 * Slots outside the preview's universe are not drawn, but for the places the TC team rejected:
 * those are drawn grey, with their reason on hover. Both tiles start at zoom 10.
 *
 * @param {SlotOverlayProps} props - The package, Type Curve scenario and the preview's ids.
 * @return {JSX.Element} The two tile sources.
 */
export function SlotOverlay({
  packageId,
  typeCurveScenarioId,
  coveredSlotIds,
  uncoveredSlotIds,
  coveredIufsIds,
  uncoveredIufsIds,
  rejectedPlaces,
}: SlotOverlayProps): JSX.Element {
  const wells = useMemo(
    () => slotPaint('uwi', coveredSlotIds, uncoveredSlotIds),
    [coveredSlotIds, uncoveredSlotIds]
  );
  const ltd = useMemo(
    () => slotPaint(LTD_PROPERTY, coveredIufsIds, uncoveredIufsIds),
    [coveredIufsIds, uncoveredIufsIds]
  );
  const rejected = useMemo(
    () => rejectedFilter(rejectedPlaces),
    [rejectedPlaces]
  );

  return (
    <>
      <Source
        id="case-slot-wells"
        type="vector"
        url={packageMapSourceUrl(
          packageId,
          'wells_v2',
          null,
          typeCurveScenarioId
        )}
      >
        {/* The not-valued sticks' white halo, under them. */}
        <Layer
          id="case-slot-wells-stick-halo"
          type="line"
          source-layer="stick"
          filter={wells.uncoveredFilter}
          paint={{
            'line-color': HALO,
            'line-width': STICK_WIDTH + 2 * HALO_WIDTH,
          }}
        />
        <Layer
          id="case-slot-wells-stick"
          type="line"
          source-layer="stick"
          filter={wells.filter}
          paint={{ 'line-color': wells.color, 'line-width': STICK_WIDTH }}
        />
        <Layer
          id="case-slot-wells-hole"
          type="circle"
          source-layer="surface-hole"
          filter={wells.filter}
          paint={{
            'circle-color': wells.color,
            'circle-radius': 3,
            'circle-stroke-color': HALO,
            'circle-stroke-width': wells.haloWidth,
          }}
        />
      </Source>
      <Source
        id="case-slot-ltd"
        type="vector"
        tiles={[ltdSlotsTileUrl(packageId, typeCurveScenarioId)]}
      >
        <Layer
          id="case-slot-ltd-stick-halo"
          type="line"
          source-layer="ltd-stick"
          filter={ltd.uncoveredFilter}
          // Solid: dashes scale with line width, so a dashed halo wouldn't line up with the stick's.
          paint={{
            'line-color': HALO,
            'line-width': STICK_WIDTH + 2 * HALO_WIDTH,
          }}
        />
        <Layer
          id="case-slot-ltd-stick"
          type="line"
          source-layer="ltd-stick"
          filter={ltd.filter}
          paint={{
            'line-color': ltd.color,
            'line-width': STICK_WIDTH,
            'line-dasharray': [2, 1],
          }}
        />
        <Layer
          id="case-slot-ltd-point"
          type="circle"
          source-layer="ltd-point"
          filter={ltd.filter}
          paint={{
            'circle-color': ltd.color,
            'circle-radius': 3,
            'circle-stroke-color': HALO,
            'circle-stroke-width': ltd.haloWidth,
          }}
        />
        {/* Unvalued in the Inventory Workspace: grey, dashed like every LTD stick, no halo. */}
        <Layer
          id={REJECTED_LAYER_IDS[0]}
          type="line"
          source-layer="ltd-stick"
          filter={rejected}
          paint={{
            'line-color': REJECTED,
            'line-width': STICK_WIDTH,
            'line-dasharray': [2, 1],
          }}
        />
        <Layer
          id={REJECTED_LAYER_IDS[1]}
          type="circle"
          source-layer="ltd-point"
          filter={rejected}
          paint={{ 'circle-color': REJECTED, 'circle-radius': 3 }}
        />
      </Source>
      {rejectedPlaces.length > 0 && (
        <RejectedPlaceTooltip places={rejectedPlaces} />
      )}
    </>
  );
}
